/* global google */
import React, { useMemo, useState, useEffect } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { Card, CardContent } from './ui/Card';
import {
    GOOGLE_MAPS_API_KEY,
    GOOGLE_MAPS_LOADER_LIBRARIES,
    GOOGLE_MAPS_LOADER_SCRIPT_ID,
} from '../config/api';

interface PublicAcademyMapProps {
    address: string;
    name: string;
    lat?: number;
    lng?: number;
}

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '0.5rem',
};

const AdvancedMarker = ({ position, map, title }: { position: google.maps.LatLngLiteral, map: google.maps.Map, title: string }) => {
    useEffect(() => {
        if (!map || !window.google || !window.google.maps.marker) return () => { };

        const pinView = new window.google.maps.marker.PinElement({
            background: "#ffffff",
            borderColor: "#d1d5db",
            glyphColor: "#2563eb",
        });

        const contentDiv = document.createElement('div');
        contentDiv.className = "flex flex-col items-center cursor-pointer";

        const labelDiv = document.createElement('div');
        labelDiv.className = "bg-white px-2 py-1 rounded shadow-sm border border-gray-200 text-xs font-semibold mb-1 whitespace-nowrap";
        labelDiv.innerText = title;

        contentDiv.appendChild(labelDiv);
        contentDiv.appendChild(pinView.element);

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
            position,
            map,
            title,
            content: contentDiv,
        });

        return () => {
            marker.map = null;
        };
    }, [map, position.lat, position.lng, title]);

    return null;
};

export default function PublicAcademyMap({ address, name, lat, lng }: PublicAcademyMapProps) {
    const { isLoaded } = useJsApiLoader({
        id: GOOGLE_MAPS_LOADER_SCRIPT_ID,
        googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
        libraries: [...GOOGLE_MAPS_LOADER_LIBRARIES],
    });

    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
    const [geocodeCenter, setGeocodeCenter] = useState<google.maps.LatLngLiteral | null>(null);

    useEffect(() => {
        if (!lat && !lng && address && isLoaded && window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    setGeocodeCenter({
                        lat: results[0].geometry.location.lat(),
                        lng: results[0].geometry.location.lng(),
                    });
                }
            });
        }
    }, [address, lat, lng, isLoaded]);

    const center = useMemo(() => {
        if (lat && lng) return { lat, lng };
        if (geocodeCenter) return geocodeCenter;
        return { lat: -37.8136, lng: 144.9631 }; // Default to Melbourne for AU context if not provided
    }, [lat, lng, geocodeCenter]);

    if (!isLoaded) {
        return (
            <Card>
                <CardContent className="flex h-[400px] items-center justify-center rounded-lg bg-muted/50 p-0">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="overflow-hidden">
            <CardContent className="p-0">
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={center}
                    zoom={15}
                    options={{
                        disableDefaultUI: false,
                        zoomControl: true,
                        scrollwheel: false,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: true,
                        mapId: 'DEMO_MAP_ID', // Required for AdvancedMarkerElement
                    }}
                    onLoad={(map) => setMapInstance(map)}
                    onUnmount={() => setMapInstance(null)}
                >
                    {mapInstance && (
                        <AdvancedMarker position={center} map={mapInstance} title={name} />
                    )}
                </GoogleMap>
                <div className="border-t border-border bg-card p-4">
                    <p className="text-sm font-medium text-foreground">{name}</p>
                    <p className="text-sm text-muted-foreground">{address}</p>
                </div>
            </CardContent>
        </Card>
    );
}
